import { useState } from "react";
import { motion } from "framer-motion";
import { X, ArrowRight } from "lucide-react";

const BRACES = [
  { name: "Knee", slug: "knee" },
  { name: "Shoulder", slug: "shoulder" },
  { name: "Ankle", slug: "ankle" },
  { name: "Back", slug: "back" },
  { name: "Elbow", slug: "elbow" },
];

const COLORWAYS = [
  { name: "Mocha", slug: "mocha", hex: "#8A6247" },
  { name: "Taupe", slug: "taupe", hex: "#B8AFA6" },
  { name: "Beige", slug: "beige", hex: "#E5DFD5" },
  { name: "Clay", slug: "clay", hex: "#A96A44" },
];

const STITCHED = [
  "Still Iconic. Just Healing.",
  "Injured, Not Invisible.",
  "Heal Loud.",
  "Torn, Not Broken.",
  "Recovery Era.",
  "Wear It Like You Mean It.",
];

export const DesignStudio = ({ onClose, onJoin }) => {
  const [brace, setBrace] = useState(0);
  const [colorway, setColorway] = useState(0);
  const [line, setLine] = useState(0);

  return (
    <motion.div
      data-testid="design-studio"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[70] bg-[#EFE8DC] overflow-y-auto"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 md:py-10 min-h-full flex flex-col">
        <div className="flex items-center justify-end">
          <button
            data-testid="studio-close-button"
            onClick={onClose}
            aria-label="Close design studio"
            className="rounded-full border border-[#2C241E]/20 p-2.5 text-[#2C241E] transition-colors duration-200 hover:bg-[#E5DFD5]"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>
        <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center py-6 md:py-8">
          <div className="md:col-span-6">
            <motion.div
              key={`${brace}-${colorway}`}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-2xl border border-[#2C241E]/15 p-5 md:p-8"
            >
              <p
                data-testid="studio-fig-label"
                className="text-[10px] font-medium tracking-[0.3em] uppercase text-[#2C241E]/50 mb-5"
              >
                Prints Available — Tap Below
              </p>
              <div className="relative rounded-xl overflow-hidden aspect-[4/5] max-h-[58vh] w-full bg-[#EFE8DC]">
                <img
                  data-testid="studio-preview-image"
                  src={`/renders/${BRACES[brace].slug}-${COLORWAYS[colorway].slug}.png`}
                  alt={`LIGATEN ${BRACES[brace].name} brace in ${COLORWAYS[colorway].name}`}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <p
                data-testid="studio-caption"
                className="text-[10px] font-medium tracking-[0.3em] uppercase text-[#2C241E]/50 mt-5 text-center"
              >
                {COLORWAYS[colorway].name} — Embroidered Front Band
              </p>
            </motion.div>
          </div>
          <div className="md:col-span-6">
            <p className="text-xs font-medium tracking-[0.3em] uppercase text-[#2C241E]/50 mb-3">
              Design It
            </p>
            <h2
              data-testid="studio-headline"
              className="font-display text-4xl md:text-6xl tracking-tighter text-[#2C241E]"
            >
              Make it yours.
            </h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-[#2C241E]/65 max-w-md">
              Pick a colorway and the line stitched across the front band. This is exactly how your
              brace would arrive.
            </p>
            <p className="mt-8 mb-3 text-[10px] font-medium tracking-[0.25em] uppercase text-[#2C241E]/45">
              Support
            </p>
            <div className="flex flex-wrap gap-2">
              {BRACES.map((b, bi) => (
                <button
                  key={b.name}
                  data-testid={`studio-brace-${b.name.toLowerCase()}`}
                  onClick={() => setBrace(bi)}
                  className={`text-xs font-medium rounded-full px-4 py-2 transition-[background-color,color,transform] duration-200 hover:-translate-y-0.5 ${
                    bi === brace
                      ? "bg-[#2C241E] text-[#EFE8DC]"
                      : "bg-[#E5DFD5] text-[#2C241E]/70 hover:bg-[#B8AFA6]/60"
                  }`}
                >
                  {b.name}
                </button>
              ))}
            </div>
            <p className="mt-7 mb-3 text-[10px] font-medium tracking-[0.25em] uppercase text-[#2C241E]/45">
              Colorway
            </p>
            <div className="flex gap-5">
              {COLORWAYS.map((cw, ci) => (
                <button
                  key={cw.name}
                  data-testid={`studio-colorway-${cw.name.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setColorway(ci)}
                  className="flex flex-col items-center gap-2"
                >
                  <span
                    className={`w-9 h-9 rounded-full transition-[transform,box-shadow] duration-200 hover:scale-110 ${
                      ci === colorway
                        ? "ring-2 ring-[#2C241E] ring-offset-2 ring-offset-[#EFE8DC]"
                        : "border border-[#2C241E]/20"
                    }`}
                    style={{ backgroundColor: cw.hex }}
                  />
                  <span className="text-[9px] font-medium tracking-[0.15em] uppercase text-[#2C241E]/55">
                    {cw.name}
                  </span>
                </button>
              ))}
            </div>
            <p className="mt-7 mb-3 text-[10px] font-medium tracking-[0.25em] uppercase text-[#2C241E]/45">
              Stitched Line
            </p>
            <div className="flex flex-wrap gap-2">
              {STITCHED.map((q, qi) => (
                <button
                  key={q}
                  data-testid={`studio-quote-${qi}`}
                  onClick={() => setLine(qi)}
                  className={`text-xs font-medium rounded-full px-4 py-2 transition-[background-color,color,transform] duration-200 hover:-translate-y-0.5 ${
                    qi === line
                      ? "bg-[#2C241E] text-[#EFE8DC]"
                      : "bg-[#E5DFD5] text-[#2C241E]/70 hover:bg-[#B8AFA6]/60"
                  }`}
                >
                  {q}
                </button>
              ))}
            </div>
            <button
              data-testid="studio-join-button"
              onClick={() =>
                onJoin({
                  brace: BRACES[brace].name,
                  quote: STITCHED[line],
                  colorway: COLORWAYS[colorway].name,
                })
              }
              className="mt-10 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2C241E] text-[#EFE8DC] text-xs md:text-sm font-medium tracking-[0.2em] uppercase px-8 py-4 transition-[transform,background-color] duration-200 hover:bg-[#3A322C] hover:-translate-y-0.5"
            >
              Join the Waitlist
              <ArrowRight size={16} strokeWidth={1.75} />
            </button>
            <p data-testid="studio-selection" className="mt-3 text-xs text-center text-[#2C241E]/50">
              {BRACES[brace].name} · {COLORWAYS[colorway].name} · {STITCHED[line]}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
